import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, idr, normalizeImageUrl } from "@/lib/api";
import { Award, FileText, Users, Building2, ArrowRight, CheckCircle2, ShieldCheck, GraduationCap, Sparkles } from "lucide-react";

export default function Home() {
  const [settings, setSettings] = useState(null);
  const [schemes, setSchemes] = useState([]);
  const [news, setNews] = useState([]);
  useEffect(() => {
    api.get("/settings").then(r => setSettings(r.data));
    api.get("/schemes").then(r => setSchemes(r.data));
    api.get("/news").then(r => setNews(r.data.slice(0, 3)));
  }, []);
  const hero = settings?.hero || {};
  const stats = settings?.stats || { schemes: 7, assessors: 10, fee_million: 1 };
  const about = settings?.about || {};

  const heroImages = useMemo(() => {
    const fromSettings = Array.isArray(hero.images) && hero.images.length
      ? hero.images
      : [hero.image_url,
         "https://images.unsplash.com/photo-1714974528737-3e6c7e4d11af?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
         "https://images.unsplash.com/photo-1714974528737-3e6c7e4d11af?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
         "https://images.unsplash.com/photo-1714974528737-3e6c7e4d11af?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
         "https://images.unsplash.com/photo-1714974528737-3e6c7e4d11af?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"];
    return fromSettings.map(normalizeImageUrl).filter(Boolean);
  }, [hero.images, hero.image_url]);
  const [slide, setSlide] = useState(0);
  useEffect(() => {
    if (heroImages.length < 2) return;
    const t = setInterval(() => setSlide(s => (s + 1) % heroImages.length), 10000);
    return () => clearInterval(t);
  }, [heroImages.length]);

  const quickAccess = [
    { icon: Award, title: "Skema Sertifikasi", desc: "Lihat pilihan skema sertifikasi", to: "/skema" },
    { icon: FileText, title: "Pendaftaran", desc: "Daftar sertifikasi secara online", to: "/daftar" },
    { icon: Users, title: "Asesor", desc: "Kenali asesor kompetensi kami", to: "/asesor" },
    { icon: Building2, title: "TUK", desc: "Temukan Tempat Uji Kompetensi", to: "/tuk" },
  ];

  return (
    <div>
      {/* HERO */}
      <section data-testid="hero-section" className="relative min-h-[85vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          {heroImages.map((src, i) => (
            <div key={i} className="absolute inset-0 transition-transform duration-1000 ease-in-out"
              style={{ transform: `translateX(${(i - slide) * 100}%)` }}>
              <img src={src} alt="Sertifikasi Realestat" className="w-full h-full object-cover hero-zoom" />
            </div>
          ))}
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 w-full">
          <div className="max-w-3xl fade-in-up">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-amber-300/30 mb-6">
              <Sparkles className="w-3.5 h-3.5 text-amber-300" />
              <span className="text-xs font-semibold tracking-widest uppercase text-amber-200">Lembaga Sertifikasi Profesi</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.1] tracking-tight">
              {hero.title || "Sertifikasi Kompetensi Profesi Realestat Indonesia"}
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-white/85 leading-relaxed max-w-2xl">
              {hero.subtitle}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/skema" data-testid="hero-cta-schemes" className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl bg-gradient-to-r from-yellow-400 via-amber-500 to-amber-600 text-emerald-950 font-bold shadow-2xl shadow-amber-500/20 hover:shadow-amber-500/40 hover:scale-[1.02] transition-all">
                {hero.cta_secondary || "Lihat Skema Sertifikasi"} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* QUICK ACCESS */}
      <section data-testid="quick-access" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 relative z-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {quickAccess.map((q, i) => (
            <Link key={i} to={q.to} data-testid={`quick-access-${q.title.toLowerCase().replace(/\s+/g,'-')}`}
              className="group bg-white rounded-2xl p-6 shadow-xl shadow-emerald-950/5 hover:shadow-2xl hover:-translate-y-1 transition-all border border-slate-100">
              <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center mb-4 group-hover:bg-emerald-950 transition-colors">
                <q.icon className="w-6 h-6 text-emerald-950 group-hover:text-amber-300 transition-colors" />
              </div>
              <div className="font-display font-bold text-lg text-emerald-950">{q.title}</div>
              <div className="text-sm text-slate-600 mt-1">{q.desc}</div>
              <div className="mt-4 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-emerald-700 group-hover:gap-2 transition-all">
                Selengkapnya <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ABOUT */}
      <section data-testid="about-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700 mb-4">Tentang LSP</div>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-emerald-950 tracking-tight">
              {about.title || "Tentang LSP Realestat Indonesia"}
            </h2>
            <p className="mt-6 text-lg text-slate-700 leading-relaxed">{about.body}</p>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {(about.points || []).map((p, i) => (
                <div key={i} className="flex gap-3">
                  <CheckCircle2 className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-emerald-950">{p.title}</div>
                    <div className="text-sm text-slate-600">{p.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <img src="https://lh3.googleusercontent.com/d/1DWjFIT-vsynETT9TenfLoxAduAoC1nga" alt="Profesional Realestat" className="rounded-2xl shadow-2xl" />
            <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-xl border border-slate-100 hidden sm:block">
              <div className="flex items-center gap-3">
                <ShieldCheck className="w-8 h-8 text-emerald-700" />
                <div>
                  <div className="font-display font-bold text-emerald-950">Terpercaya</div>
                  <div className="text-xs text-slate-600">Sertifikasi Profesi Nasional</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section data-testid="stats-section" className="bg-emerald-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-1 sm:grid-cols-3 gap-10 text-center">
          <div data-testid="stat-schemes"><div className="font-display text-5xl sm:text-6xl font-extrabold gold-gradient-text">{stats.schemes}</div><div className="mt-2 text-sm font-semibold tracking-widest uppercase text-white/70">Skema Sertifikasi</div></div>
          <div data-testid="stat-assessors"><div className="font-display text-5xl sm:text-6xl font-extrabold gold-gradient-text">{stats.assessors}</div><div className="mt-2 text-sm font-semibold tracking-widest uppercase text-white/70">Asesor</div></div>
          <div data-testid="stat-fee"><div className="font-display text-4xl sm:text-5xl font-extrabold gold-gradient-text">Rp1.000.000</div><div className="mt-2 text-sm font-semibold tracking-widest uppercase text-white/70">Biaya Pendaftaran</div></div>
        </div>
      </section>

      {/* SCHEMES PREVIEW */}
      <section data-testid="schemes-preview" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4 mb-10">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700 mb-3">Skema Sertifikasi</div>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-emerald-950">7 Skema Kompetensi</h2>
          </div>
          <Link to="/skema" className="text-sm font-bold text-emerald-800 hover:text-amber-600 inline-flex items-center gap-1">Lihat semua <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...schemes].sort((a, b) => (a.code || "").localeCompare(b.code || "")).map(s => (
            <Link key={s.id} to={`/skema/${s.id}`} data-testid={`scheme-card-${s.code}`}
              className="group bg-white rounded-2xl p-7 border border-slate-100 hover:border-amber-300 hover:shadow-xl transition-all">
              <div className="flex items-center justify-between mb-4">
                <div className="font-display text-4xl font-extrabold gold-gradient-text">{s.code}</div>
                <GraduationCap className="w-8 h-8 text-emerald-700/40 group-hover:text-emerald-700 transition-colors" />
              </div>
              <h3 className="font-display font-bold text-lg text-emerald-950 leading-snug line-clamp-3">{s.title}</h3>
              <p className="text-sm text-slate-600 mt-3 line-clamp-2">{s.short_desc}</p>
              <div className="mt-5 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-emerald-700">Lihat Detail <ArrowRight className="w-3.5 h-3.5" /></div>
            </Link>
          ))}
        </div>
      </section>

      {/* NEWS */}
      {news.length > 0 && (
        <section data-testid="news-preview" className="bg-white border-y border-slate-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="flex justify-between items-end mb-10">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700 mb-3">Berita Terbaru</div>
                <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-emerald-950">Informasi & Kegiatan</h2>
              </div>
              <Link to="/berita" className="text-sm font-bold text-emerald-800 hover:text-amber-600 hidden sm:inline-flex items-center gap-1">Semua berita <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {news.map(n => (
                <Link key={n.id} to={`/berita/${n.id}`} className="group">
                  <div className="aspect-[16/10] rounded-xl overflow-hidden mb-4">
                    <img src={normalizeImageUrl(n.photo_url)} alt={n.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="text-xs font-bold uppercase tracking-widest text-amber-600">{n.category}</div>
                  <h3 className="font-display font-bold text-lg text-emerald-950 mt-2 leading-snug line-clamp-2">{n.title}</h3>
                  <p className="text-sm text-slate-600 mt-2 line-clamp-2">{n.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="rounded-3xl overflow-hidden relative bg-gradient-to-br from-emerald-950 via-emerald-900 to-teal-900 p-10 lg:p-16">
          <div className="absolute inset-0 opacity-10" style={{backgroundImage: "radial-gradient(circle at 20% 20%, #D4AF37 0%, transparent 50%)"}} />
          <div className="relative grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="font-display text-3xl lg:text-5xl font-extrabold text-white leading-tight">Siap Menjadi Profesional Tersertifikasi?</h2>
              <p className="mt-4 text-white/80 text-lg">Daftar sertifikasi kompetensi realestat sekarang — biaya pendaftaran hanya <span className="text-amber-300 font-bold">{idr(settings?.payment?.amount || 1000000)}</span>.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 lg:justify-end">
              <Link to="/daftar" className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl gold-gradient text-emerald-950 font-bold shadow-2xl">Daftar Sekarang <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/cek-sertifikat" className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl border-2 border-white/30 text-white font-semibold hover:bg-white/10">Cek Sertifikat</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
